'use client';

/**
 * Session-lost signal (#2700).
 *
 * One place that answers: "was this browser signed in a moment ago, and is it
 * not signed in now?" That is a different question from "is the user signed
 * out", which is the ordinary state on every public page — this is
 * specifically the *transition* that leaves a staff member staring at a
 * dashboard that has quietly stopped working.
 *
 * Why a module-level store instead of React context alone: `authedFetch` is a
 * plain function called from inside `useEffect` polling loops, so it cannot
 * read context. Both the fetch wrapper and the banner need the same flag, so
 * the flag lives here and React subscribes to it.
 *
 * DELIBERATE: nothing here decides *on its own* that the session is gone.
 * Callers must confirm first (see `confirmSessionGone` in authedFetch.ts).
 * A single 401, or a `SIGNED_OUT` event, is not proof — both fire transiently
 * during ordinary token rotation, and a false banner during a provider blip
 * would be worse than the silence it replaces.
 */

type Listener = (lost: boolean) => void;

let sessionLost = false;
let intentionalSignOut = false;
/**
 * Bumped every time the session is known to be alive again. Detached
 * confirmations capture it before they start and are discarded if it moved
 * while they were in flight — otherwise a slow "yes, it's gone" resolving
 * AFTER the session came back re-raises a false banner. Review of #2709 drove
 * exactly that interleave.
 */
let generation = 0;
const listeners = new Set<Listener>();

/** True once the session has been CONFIRMED gone after having been present. */
export function isSessionLost(): boolean {
  return sessionLost;
}

/**
 * Record that the user is signing out on purpose.
 *
 * Supabase fires the same `SIGNED_OUT` event whether the user clicked "Sign
 * out" or their session evaporated. Without this flag the banner would appear
 * on every deliberate sign-out, which is both wrong and alarming.
 *
 * Cleared ONLY by `clearSessionLost()`, which `AuthContext` calls on every auth
 * event that carries a session. `markSessionLost()` does NOT clear it — it
 * early-returns — so this comment previously claimed a self-clearing behavior
 * that did not exist. If the only caller of `clearSessionLost()` ever narrows
 * back to the password sign-in path, this flag latches and the banner is
 * suppressed forever for that tab (the emailed-code path goes through
 * `verifyOtp`, not `signIn()`).
 */
export function markIntentionalSignOut(): void {
  intentionalSignOut = true;
}

export function isIntentionalSignOut(): boolean {
  return intentionalSignOut;
}

/**
 * Flag the session as confirmed-lost. No-ops during a deliberate sign-out and
 * when already flagged, so repeated 401s from a dozen parallel polls raise the
 * banner once rather than re-rendering the tree on every failed request.
 */
export function markSessionLost(atGeneration?: number): void {
  // A confirmation that started before the session came back is stale — drop it.
  if (atGeneration !== undefined && atGeneration !== generation) return;
  if (intentionalSignOut) return;
  if (sessionLost) return;
  sessionLost = true;
  listeners.forEach((listener) => listener(true));
}

/** Snapshot to hand to `markSessionLost` from a detached confirmation. */
export function currentSessionGeneration(): number {
  return generation;
}

/**
 * The session is alive again, but we are NOT necessarily "signed in afresh".
 *
 * Clears the lost flag and invalidates in-flight confirmations, while LEAVING
 * the intentional-sign-out marker alone. Use this on recovery paths driven by
 * a successful request, not by an auth event.
 *
 * Why the distinction (review finding on #2709): a deliberate sign-out sets
 * the marker, but the session is still alive for the moments before Supabase
 * tears it down. A recovery check landing in that window used to wipe the
 * marker, so the subsequent SIGNED_OUT raised a banner on a user who had just
 * clicked "Sign out" — defeating a guard this PR otherwise mutation-tests.
 */
export function noteSessionAlive(): void {
  generation += 1;
  if (!sessionLost) return;
  sessionLost = false;
  listeners.forEach((listener) => listener(false));
}

/**
 * Reset on a genuine auth event carrying a session. Also clears the
 * intentional-sign-out marker, because a real sign-in means any previous
 * sign-out is over.
 */
export function clearSessionLost(): void {
  // Bump unconditionally, even when nothing was flagged: this is the moment we
  // know the session is alive, so any confirmation still in flight is stale.
  generation += 1;
  intentionalSignOut = false;
  if (!sessionLost) return;
  sessionLost = false;
  listeners.forEach((listener) => listener(false));
}

export function subscribeToSessionLost(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: reset module state between cases. */
export function __resetSessionLostForTests(): void {
  sessionLost = false;
  intentionalSignOut = false;
  generation = 0;
  listeners.clear();
}
